import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, patch } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

/** Normalize thunk arg for slice selectors (`string` or `{ projectId, context? }`). */
export function getProjectIdFromFetchStatusesArg(arg) {
  if (arg == null) return "";
  if (typeof arg === "string") return String(arg).trim();
  if (typeof arg === "object" && !Array.isArray(arg)) {
    return String(arg.projectId ?? arg.id ?? "").trim();
  }
  return "";
}

/**
 * @param {string | { projectId: string; context?: string }} arg
 * `context` becomes a benign `_ctx` query param so GET dedupe does not cancel a different screen's in-flight statuses fetch.
 */
export const fetchProjectStatuses = createAsyncThunk(
  "projectStatuses/fetchByProjectId",
  async (arg, { rejectWithValue }) => {
    const projectId = getProjectIdFromFetchStatusesArg(arg);
    const context =
      typeof arg === "object" &&
      arg != null &&
      !Array.isArray(arg) &&
      typeof arg.context === "string"
        ? arg.context.trim()
        : "";
    if (!projectId) {
      return rejectWithValue("Project id is required");
    }
    try {
      const params = context ? { _ctx: context } : {};
      const response = await get(API_ENDPOINTS.PROJECTS.GET_STATUSES(projectId), params);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project statuses"));
    }
  }
);

export const createProjectStatus = createAsyncThunk(
  "projectStatuses/create",
  async ({ projectId, body }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.CREATE_STATUS(projectId);
      const response = await post(endpoint, body, {}, { "Content-Type": "application/json" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create status"));
    }
  }
);

export const updateProjectStatus = createAsyncThunk(
  "projectStatuses/update",
  async ({ projectId, statusId, body }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.UPDATE_STATUS(projectId, statusId);
      const response = await patch(endpoint, body, {}, { "Content-Type": "application/json" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update status"));
    }
  }
);
